import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getDuration, useReliableInterval, useReliableTimeout } from '@common-fe/common-fe';

import { MINUTE, SECOND } from '@/common/constants';
import { ChangeVerificationMethodModal, SendCodeModal } from '@/modules/user/components/SignIn/MultiFactorAuthentication/components';
import { MfaServerErrorText, MFAStep } from '@/modules/user/components/SignIn/MultiFactorAuthentication/mfa.types';
import useSendMFACodeQuery from '@/modules/user/components/SignIn/MultiFactorAuthentication/queries/useSendMFACode.query';
import useMFAStore from '@/modules/user/components/SignIn/MultiFactorAuthentication/stores/useMFA.store';

const ONE_MINUTE_IN_SECONDS = MINUTE / SECOND;

interface Props {
  onSetVisible: (value: boolean) => void;
  onShowAccountLockedModal: (value: number) => void;
}

const MultiFactorAuthenticationModals: React.FC<Props> = ({ onSetVisible, onShowAccountLockedModal }) => {
  const [step, setStep] = useState(MFAStep.SEND_CODE);
  const { activeChannel, session, isRequiredExpressSetup, setChannelCodeSentTimestamp, resetStore } = useMFAStore();
  const { sendMFACode } = useSendMFACodeQuery({ isRequiredExpressSetup }); 
  const resendTimeout = useRef(0);
  const [resetTimer, setResetTimer] = useState(false);
  const [sendCodeErrorText, setSendCodeErrorText] = useState('');

  const handleResetTimer = () => {
    setResetTimer((prev) => !prev);
  };

  const handleSendMFACode = useCallback(async () => {
    try {
      setSendCodeErrorText('');
      if (activeChannel && session) {
        await sendMFACode({
          channel: activeChannel,
          session: session,
        });
        setChannelCodeSentTimestamp(activeChannel, new Date().getTime());
        handleResetTimer();
        resendTimeout.current = ONE_MINUTE_IN_SECONDS;
      }
    } catch (error) {
      // @ts-ignore
      if (error?.response?.data?.elevate_error_message?.includes(MfaServerErrorText.SESSION_IS_EXPIRED)) {
        onSetVisible(false);
      }
    }
  }, [activeChannel, session, sendMFACode, onSetVisible, setChannelCodeSentTimestamp]);

  const handleUpdateTimeout = useCallback((timeoutInSeconds: number) => {
    resendTimeout.current = timeoutInSeconds;
  }, []);

  const { start: startReliableInterval, stop: stopReliableInterval } =  useReliableInterval(() => {
    resendTimeout.current = resendTimeout.current - 1;
  }, getDuration({ seconds: 1 }));

  useEffect(() => {
    startReliableInterval(); 
    return () => {
      stopReliableInterval();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { start: startReliableTimeout, clear: clearReliableTimeout } =  useReliableTimeout(() => {
    onSetVisible(false);
  },getDuration({ minutes: 10 }));
  useEffect(() => {
    startReliableTimeout();
    return () => {
      clearReliableTimeout();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTimer]);

  useEffect(() => {
    if (activeChannel?.value && resendTimeout.current <= 0) {
      handleSendMFACode();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel?.value]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {step === MFAStep.SEND_CODE && (
        <SendCodeModal
          onSetVisible={onSetVisible}
          setStep={setStep}
          onResendCode={handleSendMFACode}
          resendTimeout={resendTimeout.current}
          sendCodeErrorText={sendCodeErrorText}
          onShowAccountLockedModal={onShowAccountLockedModal}
        />
      )}
      {step === MFAStep.CHANGE_VERIFICATION_METHOD && (
        <ChangeVerificationMethodModal
          onSetVisible={onSetVisible}
          setStep={setStep}
          onUpdateTimeout={handleUpdateTimeout}
        />
      )}
    </>
  );
};

export default MultiFactorAuthenticationModals;
